import type { EditorJsData } from '../../editorjs-types';
import type { AttachUploadLinkHandlers } from './tools/files/editorjs-attach-upload-link-tool';
import type {
  GalleryEditRequest,
  GalleryImageViewRequest,
} from './tools/gallery/editorjs-gallery-tool';
import type { AiInstructionsEditRequest } from './tunes/editorjs-ai-instructions-tune';
import type { EditorJsEditorHandle } from './utils/editor-js-editor-handle';
import type { EditorDynamicConfig } from './utils/use-editor-setup';

import { forwardRef, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import './editor.scss';
import { useAppSettings } from '#providers/app-settings-provider';

import { useGalleryImageModal } from './tools/gallery/gallery-image-modal-context';
import { useDocumentMermaidVisibility } from './tools/mermaid/document-mermaid-visibility-context';
import { useCommitEditorOutput } from './utils/use-commit-editor-output';
import { useEditorHolderLinkNavigation } from './utils/use-editor-holder-link-navigation';
import { useEditorJsImperativeHandle } from './utils/use-editor-js-imperative-handle';
import {
  useDocumentHeadingPortalNodes,
  useFilesPortalNodes,
} from './utils/use-editor-portal-nodes';
import { useEditorSetup } from './utils/use-editor-setup';
import { usePortalTargetMap } from './utils/use-portal-target-map';
import { useSchedulePersist } from './utils/use-schedule-persist';
import { useSyncEditorValueFromProp } from './utils/use-sync-editor-value-from-prop';

export type { EditorJsEditorHandle } from './utils/editor-js-editor-handle';

interface EditDocumentEditorProps {
  /** Initial content as Editor.js data. */
  value?: EditorJsData | null;
  /** Document id for AI processing prompts (undefined for new/unsaved docs). */
  documentId?: string;
  /** API origin for upload links in the Files block (e.g. `https://api.example.com`). */
  apiHost: string;
  /** Signed-in user email (Files block upload modal). */
  userEmail: string;
  /** Whether this is a new document (not yet saved). */
  isNewDocument: boolean;
  /** Dim editor body text until the user changes content (e.g. unsaved path). */
  editorPlaceholderMuted?: boolean;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Current document path for context-aware tools like Subpages. */
  parentPath: string;
  /** Current child documents for the Subpages block. */
  children: Array<{ id: string; path: string; label?: string }>;
  /** Open a React-powered prompt to create a subpage. */
  onCreateSubpage?: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  /** Add a block reference to the document chat composer (block tune). */
  onAddToChat?: (blockId: string) => void;
  /** Open React UI to edit a gallery block as JSON (block settings). */
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  /**
   * Open full-size gallery image preview (from `GalleryImageModalProvider` /
   * `useGalleryImageModal`).
   */
  onOpenGalleryImage?: (request: GalleryImageViewRequest) => void;
  /** Open React UI to edit per-block AI instructions (block tune). */
  onRequestAiInstructionsEdit?: (request: AiInstructionsEditRequest) => void;
  /** Open rename document dialog (document heading leaf segment). */
  onRequestRename?: () => void;
  /** Open upload flow to link selected text to an uploaded file. */
  onRequestAttachUploadLink?: (handlers: AttachUploadLinkHandlers) => void;
}

export const EditDocumentEditor = forwardRef<
  EditorJsEditorHandle,
  EditDocumentEditorProps
>(function EditDocumentEditor(
  {
    value,
    documentId,
    apiHost,
    userEmail,
    onUpload,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
    onAddToChat,
    onRequestGalleryEdit,
    onOpenGalleryImage,
    onRequestAiInstructionsEdit,
    onRequestRename,
    onRequestAttachUploadLink,
    isNewDocument,
    editorPlaceholderMuted = false,
  },
  ref,
) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any | null>(null);
  /** Serializes `api.saver.save()` / `editor.save()` so out-of-order completions cannot clobber `latestValueRef`. */
  const persistChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const latestValueRef = useRef<EditorJsData | null>(value ?? null);
  const navigate = useNavigate();
  const { documentPathRegex } = useAppSettings();
  const { isSourceVisible, setSourceVisible } = useDocumentMermaidVisibility();
  const openGalleryImageFromContext = useGalleryImageModal();

  const dynamicConfigRef = useRef<EditorDynamicConfig>({
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
    onRequestGalleryEdit,
    onOpenGalleryImage,
    onRequestAiInstructionsEdit,
    onRequestAttachUploadLink,
  });
  dynamicConfigRef.current = {
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
    onRequestGalleryEdit,
    onOpenGalleryImage: onOpenGalleryImage ?? openGalleryImageFromContext,
    onRequestAiInstructionsEdit,
    onRequestAttachUploadLink,
  };

  const commitEditorOutput = useCommitEditorOutput({
    latestValueRef,
    documentPathRegex,
  });

  const schedulePersist = useSchedulePersist(persistChainRef);

  const {
    targets: documentHeadingPortalTargets,
    register: registerDocumentHeadingPortalTarget,
    unregister: unregisterDocumentHeadingPortalTarget,
    setTargets: setDocumentHeadingPortalTargets,
  } = usePortalTargetMap();

  const {
    targets: filesPortalTargets,
    register: registerFilesPortalTarget,
    unregister: unregisterFilesPortalTarget,
    setTargets: setFilesPortalTargets,
  } = usePortalTargetMap();

  const documentHeadingPortalNodes = useDocumentHeadingPortalNodes({
    targets: documentHeadingPortalTargets,
    parentPath,
    childDocuments: children,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
  });

  const filesPortalNodes = useFilesPortalNodes({
    targets: filesPortalTargets,
    documentId,
    apiHost,
    userEmail,
    parentPath,
  });

  useEditorSetup({
    holderRef,
    editorRef,
    persistChainRef,
    latestValueRef,
    dynamicConfigRef,
    commitEditorOutput,
    schedulePersist,
    registerDocumentHeadingPortalTarget,
    unregisterDocumentHeadingPortalTarget,
    registerFilesPortalTarget,
    unregisterFilesPortalTarget,
    setDocumentHeadingPortalTargets,
    setFilesPortalTargets,
  });

  useEditorHolderLinkNavigation(holderRef, { documentPathRegex, navigate });

  useSyncEditorValueFromProp({
    editorRef,
    persistChainRef,
    latestValueRef,
    value,
  });

  useEditorJsImperativeHandle(ref, {
    editorRef,
    persistChainRef,
    latestValueRef,
    schedulePersist,
    commitEditorOutput,
  });

  return (
    <div
      className={
        editorPlaceholderMuted
          ? 'edit-document-editor-root edit-document-editor-root--new-placeholder-muted'
          : 'edit-document-editor-root'
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: '300px',
      }}
    >
      {documentHeadingPortalNodes}
      {filesPortalNodes}
      <div
        style={{
          flex: 1,
          minHeight: '300px',
          width: '100%',
        }}
      >
        <div
          ref={holderRef}
          style={{
            height: '100%',
            width: '100%',
            minHeight: '300px',
          }}
          className="editorjs-root editorjs-holder"
        />
      </div>
    </div>
  );
});
